/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
/* eslint-disable no-shadow */
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useSelector } from 'react-redux';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import {
  Button,
  ButtonGroup,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getToolTipHTML } from './getToolTipHTML';
import d3 from '../../utils/d3Importer';
import { getMapboxToken, getColour } from '../../utils/functions';
import { MapStyles } from './mapStyles';

const MAPBOX_ACCESS_TOKEN = getMapboxToken();
const medianStyle = 'mapbox://styles/sbsonline/cmi2l4mlx00dl01rb19dsbybz';
const meanStyle = 'mapbox://styles/sbsonline/cmi3n52r0006901st42fzdxhz';

const capitalViews = {
  australia: {
    label: 'Australia',
    center: [134, -27],
    zoom: window.innerWidth > 600 ? 3.1 : 2.5,
  },
  sydney: {
    label: 'Sydney',
    center: [151.2093, -33.8688],
    zoom: 9,
  },
  melbourne: {
    label: 'Melbourne',
    center: [144.9631, -37.8136],
    zoom: 9,
  },
  brisbane: {
    label: 'Brisbane',
    center: [153.0251, -27.4698],
    zoom: 9,
  },
  perth: {
    label: 'Perth',
    center: [115.857, -31.9505],
    zoom: 9,
  },
  adelaide: {
    label: 'Adelaide',
    center: [138.6007, -34.9285],
    zoom: 9,
  },
  hobart: {
    label: 'Hobart',
    center: [147.3272, -42.8821],
    zoom: 9,
  },
  canberra: {
    label: 'Canberra',
    center: [149.131, -35.2809],
    zoom: 9,
  },
  darwin: {
    label: 'Darwin',
    center: [130.8456, -12.4634],
    zoom: 9,
  },
};

// Rough bbox for Australia (tweak to taste)
const AUSTRALIA_BOUNDS = [
  [112, -44], // SW
  [154, -10], // NE
];

function getInitialViewForWidth() {
  const w = window.innerWidth;

  if (w >= 1200) {
    // big desktop
    return {
      bounds: AUSTRALIA_BOUNDS,
      padding: { top: 40, bottom: 40, left: 320, right: 40 }, // extra room for sidebar
    };
  }

  if (w >= 768) {
    // tablet / small laptop
    return {
      bounds: AUSTRALIA_BOUNDS,
      padding: { top: 20, bottom: 10, left: 20, right: 10 },
    };
  }

  // mobile
  return {
    bounds: AUSTRALIA_BOUNDS,
    padding: { top: 20, bottom: 20, left: 10, right: 10 },
  };
}

export default function Map() {
  const mapContainer = useRef();
  const average = useSelector((state) => state.data.average);
  const data = useSelector((state) => state.data.data);
  const popup = useRef(
    new mapboxgl.Popup({
      className: 'elec-popup',
      closeButton: true,
      closeOnClick: true,
      width: '320px',
    }),
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const marker1 = new mapboxgl.Marker();

  function localGeocoder(query) {
    const q = query.toLowerCase();

    // filter your custom SA2 features
    const matches = data.features
      .filter((f) => f.properties.n.toLowerCase().includes(q))
      .map((f) => ({
        // REQUIRED FIELDS for Mapbox geocoder results:
        place_name: `${f.properties.n}`,
        center: f.geometry.coordinates,
        geometry: f.geometry,
        place_type: ['locality'], // or "locality" works too
        properties: f.properties,
        type: 'Feature',
      }));

    return matches;
  }

  const geocoder = new MapboxGeocoder({
    accessToken: mapboxgl.accessToken,
    placeholder: 'Search suburb or SA2',
    localGeocoder, // ← your custom function
    localGeocoderOnly: false, // keep Mapbox results too (set true if you want ONLY your list)
    countries: 'au',
    marker: false,
    flyTo: false,
    minLength: 2, // can allow shorter since it's local
    types: 'locality',
    minZoom: 5,
    mapboxgl,
  });

  const geolocate = new mapboxgl.GeolocateControl({
    positionOptions: {
      enableHighAccuracy: true,
    },
    fitBoundsOptions: { maxZoom: 11 },
    trackUserLocation: false,
    showUserHeading: false,
    showUserLocation: false,
  });

  const [map, setMap] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const currentStyleRef = useRef(medianStyle);
  const averageRef = useRef(average);

  useEffect(() => {
    averageRef.current = average;
  }, [average]);

  const timeDomain = ['2018/19', '2019/20', '2020/21', '2021/22', '2022/23'];
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

  const lineChartTotalWidth = 250;
  const lineChartTotalHeight = 160;
  const margin = { top: 5, right: 5, bottom: 35, left: 55 };
  const chartWidth = lineChartTotalWidth - margin.left - margin.right;
  const chartHeight = lineChartTotalHeight - margin.top - margin.bottom;

  // use effect for inital
  useEffect(() => {
    const initializeMap = ({ setMap, mapContainer }) => {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/sbsonline/cmi2l4mlx00dl01rb19dsbybz',
        // style: 'data/style.json',
        center: [134, -27],
        zoom: window.innerWidth > 600 ? 3.1 : 2.5,
        minZoom: 2.5,
        maxZoom: 14,
        pitch: 0,
        bearing: 0,
        cooperativeGestures: true,
        maxBounds: [
          [93, -50], // Southwest coordinates
          [176, 8], // Northeast coordinates
        ],
      });

      map.setProjection('mercator');

      map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: false,
        }),
        'top-left',
      );
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
      map.scrollZoom.disable();

      map.on('load', () => {
        // 🔑 Responsive initial view
        const { bounds, padding } = getInitialViewForWidth();
        map.fitBounds(bounds, {
          padding,
          duration: 0, // no fly animation on initial load
        });

        setMap(map);
        setMapLoaded(true);
      });
    };

    if (!map) initializeMap({ setMap, mapContainer });
  }, [map]);

  // change map style on average change
  useEffect(() => {
    // only run once the map exists and has fired 'load'
    if (!map || !mapLoaded) return;

    // close popup if open
    if (popup.current) {
      popup.current.remove();
    }

    // decide which style to use based on Redux state
    const nextStyle = average === 'mean' ? meanStyle : medianStyle;
    console.log('nextStyle ', average, nextStyle);

    // avoid calling setStyle if we're already on this style
    if (currentStyleRef.current === nextStyle) return;

    currentStyleRef.current = nextStyle;
    map.setStyle(nextStyle);
  }, [average, map, mapLoaded]);

  // create chart
  // create chart
  function createChart(e, history, chartColour) {
    // Normalise + filter data
    const series = history
      .map((d) => {
        const v = d.v === null || d.v === undefined || d.v === '' ? null : +d.v;

        return {
          y: d.y, // '2018/19', etc.
          v: Number.isNaN(v) ? null : v,
        };
      })
      .filter((d) => d.y && timeDomain.includes(d.y));

    // Nothing usable? bail
    if (!series.length) return;

    const svgElement = popup.current
      .getElement()
      .getElementsByClassName('popup-d3-svg')[0];

    if (!svgElement) return;

    const svg = d3
      .select(svgElement)
      .attr('width', lineChartTotalWidth)
      .attr('height', lineChartTotalHeight);

    // Clear previous content
    svg.select('.line-chart').selectAll('*').remove();
    svg.select('.dots-chart').selectAll('*').remove();

    // X: categorical financial years (strings)
    const x = d3
      .scalePoint()
      .domain(timeDomain) // ['2018/19','2019/20',...]
      .range([0, chartWidth])
      .padding(0.5);

    // Y: derive domain from data
    const yValues = series.filter((d) => d.v !== null).map((d) => d.v);

    // Fallback if somehow no valid numbers
    if (!yValues.length) return;

    const yMinData = d3.min(yValues);
    const yMaxData = d3.max(yValues);

    // Start at 0 if all positive (nice for this metric)
    const yMin = Math.min(0, yMinData);
    const yMax = yMaxData === yMin ? yMin + 1 : yMaxData; // avoid flat domain

    const y = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .nice()
      .range([chartHeight, 0]);

    const line = d3
      .line()
      .curve(d3.curveMonotoneX)
      .defined((d) => d.v !== null && !Number.isNaN(d.v))
      .x((d) => x(d.y))
      .y((d) => y(d.v));

    // 🔹 X AXIS: rotate tick labels ~30°
    const xAxis = (g) => {
      g.attr('transform', `translate(0,${chartHeight})`)
        .call(d3.axisBottom(x))
        .call((g2) => g2.select('.domain').remove());

      g.selectAll('text')
        .attr('text-anchor', 'end')
        .attr('dx', '-0.1em')
        .attr('dy', '0.6em')
        .attr('transform', 'rotate(-30)'); // tilt labels
    };

    // 🔹 Y AXIS: format as $ amounts
    const yAxis = (g) => {
      const formatCurrency = d3.format('$,.0f');

      const axis = d3
        .axisLeft(y)
        .ticks(3)
        .tickSize(-chartWidth)
        .tickFormat(formatCurrency);

      g.call(axis);
      g.select('.domain').remove();

      // dashed-grid except baseline
      g.selectAll('line').attr('stroke-dasharray', (d) =>
        d === 0 ? 'none' : '2,4',
      );

      // Offset text
      g.selectAll('text').attr('x', -6);

      // 🔹 Force minimum 2 ticks
      const tickCount = g.selectAll('.tick').size();

      if (tickCount < 2) {
        const [d0, d1] = y.domain();
        const mid = (d0 + d1) / 2;

        const extra = d3
          .axisLeft(y)
          .tickValues([mid])
          .tickFormat(formatCurrency)
          .tickSize(-chartWidth);

        g.call(extra);
      }
    };

    // Position inner chart group (assumes your SVG template has <g class="chart">)
    svg
      .select('.chart')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    svg.select('.x-axis').call(xAxis);
    svg.select('.y-axis').call(yAxis);

    // Line
    svg
      .select('.line-chart')
      .append('path')
      .datum(series)
      .attr('class', 'series-line')
      .attr('d', line)
      .style('fill', 'none')
      .style('stroke-width', '3px')
      .style('stroke-linecap', 'round')
      .style('stroke', chartColour);

    // Dots
    svg
      .select('.dots-chart')
      .selectAll('circle')
      .data(series)
      .join('circle')
      .attr('class', (d, i) => `dot dot${i}`)
      .attr('r', (d) => (d.v !== null && !Number.isNaN(d.v) ? 3 : 0))
      .attr('cx', (d) => x(d.y))
      .attr('cy', (d) => y(d.v))
      .style('fill', chartColour);
  }

  useEffect(() => {
    if (mapLoaded) {
      // do
      map.on('click', (e) => {
        geocoder.clear();
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['SA2Areas'],
        });
        console.log('features ', features);
        if (features && features[0]?.properties) {
          const chartColour = getColour(features[0]?.properties.f);
          popup.current
            .setLngLat(e.lngLat)
            .setHTML(
              getToolTipHTML(
                features[0]?.properties,
                chartColour,
                averageRef.current,
              ),
            )
            .setMaxWidth('300px')
            .addTo(map);

          // SVG chart start
          const meanHistory = [
            { y: '2018/19', v: features[0]?.properties.mean2018_19 },
            { y: '2019/20', v: features[0]?.properties.mean2019_20 },
            { y: '2020/21', v: features[0]?.properties.mean2020_21 },
            { y: '2021/22', v: features[0]?.properties.mean2021_22 },
            { y: '2022/23', v: features[0]?.properties.mean2022_23 },
          ];
          const medianHistory = [
            { y: '2018/19', v: features[0]?.properties.m2018_19 },
            { y: '2019/20', v: features[0]?.properties.m2019_20 },
            { y: '2020/21', v: features[0]?.properties.m2020_21 },
            { y: '2021/22', v: features[0]?.properties.m2021_22 },
            { y: '2022/23', v: features[0]?.properties.m2022_23 },
          ];
          const history1 =
            averageRef.current === 'mean' ? meanHistory : medianHistory;

          createChart(e, history1, chartColour);

          if (marker1) {
            marker1.remove();
          }
          marker1.setLngLat(e.lngLat).addTo(map);

          const newPos = e.lngLat;
          const offSet = window.innerWidth > 500 ? 0.25 : 0.21;
          newPos.lat = e.lngLat.lat - offSet;

          map.flyTo({
            center: newPos,
            zoom: 9,
          });
        }
      });

      map.addControl(geocoder);
      geocoder.on('result', (e) => {
        // console.log('geocoder on result ', e);
        const evtResultCoords = e.result.center;
        if (marker1) {
          marker1.remove();
        }
        marker1.setLngLat(e.result.center).addTo(map);

        function onMoveEnd() {
          map.off('moveend', onMoveEnd);
          const features = map.queryRenderedFeatures(
            map.project(evtResultCoords),
            {
              layers: ['SA2Areas'],
            },
          );
          if (features[0] && features[0].properties) {
            const chartColour = getColour(features[0]?.properties.f);
            popup.current
              .setLngLat(evtResultCoords)
              .setHTML(
                getToolTipHTML(
                  features[0].properties,
                  chartColour,
                  averageRef.current,
                ),
              )
              .setMaxWidth('300px')
              .addTo(map);

            // SVG chart start
            // SVG chart start
            const meanHistory = [
              { y: '2018/19', v: features[0]?.properties.mean2018_19 },
              { y: '2019/20', v: features[0]?.properties.mean2019_20 },
              { y: '2020/21', v: features[0]?.properties.mean2020_21 },
              { y: '2021/22', v: features[0]?.properties.mean2021_22 },
              { y: '2022/23', v: features[0]?.properties.mean2022_23 },
            ];
            const medianHistory = [
              { y: '2018/19', v: features[0]?.properties.m2018_19 },
              { y: '2019/20', v: features[0]?.properties.m2019_20 },
              { y: '2020/21', v: features[0]?.properties.m2020_21 },
              { y: '2021/22', v: features[0]?.properties.m2021_22 },
              { y: '2022/23', v: features[0]?.properties.m2022_23 },
            ];
            const history =
              averageRef.current === 'mean' ? meanHistory : medianHistory;
            createChart(e, history, chartColour);
          }
        }

        // console.log('e.result.center ', evtResultCoords);
        const newPos = [...evtResultCoords];
        const offSet = window.innerWidth > 500 ? 0.25 : 0.21;
        newPos[1] -= offSet;

        map.flyTo({
          center: newPos,
          zoom: 9,
        });

        map.on('moveend', onMoveEnd);
      });

      map.addControl(geolocate, 'top-left');
      geolocate.on('geolocate', (e) => {
        console.log('A geolocate event has occurred. ', e);
        if (marker1) {
          marker1.remove();
        }
        marker1.setLngLat([e.coords.longitude, e.coords.latitude]).addTo(map);

        // [e.coords.longitude, e.coords.latitude]
        const evtGeoLocate = [e.coords.longitude, e.coords.latitude];
        function onGeoEnd() {
          map.off('moveend', onGeoEnd);
          // console.log('coords ', evtGeoLocate);
          const features = map.queryRenderedFeatures(
            map.project(evtGeoLocate),
            {
              layers: ['SA2Areas'],
            },
          );
          if (features[0] && features[0].properties) {
            const chartColour = getColour(features[0]?.properties.f);
            popup.current
              .setLngLat(evtGeoLocate)
              .setHTML(
                getToolTipHTML(
                  features[0].properties,
                  chartColour,
                  averageRef.current,
                ),
              )
              .setMaxWidth('300px')
              .addTo(map);

            // SVG chart start
            // SVG chart start
            const meanHistory = [
              { y: '2018/19', v: features[0]?.properties.mean2018_19 },
              { y: '2019/20', v: features[0]?.properties.mean2019_20 },
              { y: '2020/21', v: features[0]?.properties.mean2020_21 },
              { y: '2021/22', v: features[0]?.properties.mean2021_22 },
              { y: '2022/23', v: features[0]?.properties.mean2022_23 },
            ];
            const medianHistory = [
              { y: '2018/19', v: features[0]?.properties.m2018_19 },
              { y: '2019/20', v: features[0]?.properties.m2019_20 },
              { y: '2020/21', v: features[0]?.properties.m2020_21 },
              { y: '2021/22', v: features[0]?.properties.m2021_22 },
              { y: '2022/23', v: features[0]?.properties.m2022_23 },
            ];
            const history =
              averageRef.current === 'mean' ? meanHistory : medianHistory;
            createChart(e, history, chartColour);

            const newPos = [e.coords.longitude, e.coords.latitude];
            const offSet = window.innerWidth > 500 ? 0.25 : 0.21;
            newPos[1] -= offSet;

            map.flyTo({
              center: newPos,
              zoom: 9,
            });
          }
        }

        map.on('moveend', onGeoEnd);
      });
    }
  }, [mapLoaded]);

  const handleCapitalFlyTo = (key) => {
    if (!map) return;

    const v = capitalViews[key];
    if (!v) return;

    // optional: clean up popup/marker
    popup.current?.remove();
    marker1?.remove();

    map.flyTo({
      center: v.center,
      zoom: v.zoom,
      pitch: 0,
      bearing: 0,
      essential: true,
    });
  };

  return (
    <MapStyles>
      <div id="map-wrap">
        <div ref={mapContainer} className="mapContainer" />
      </div>

      {/* 🔹 Capital city quick links */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 2,
          mb: 2,
          px: 1,
        }}
      >
        {isMobile ? (
          // Mobile: wrap nicely, 2 per row
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              width: '100%',
              maxWidth: 420,
              justifyContent: 'center',
            }}
          >
            {Object.entries(capitalViews).map(([key, cfg]) => (
              <Button
                key={key}
                variant="outlined"
                size="small"
                onClick={() => handleCapitalFlyTo(key)}
                sx={{
                  flex: '1 1 calc(50% - 8px)', // ~2 per row on mobile
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {cfg.label}
              </Button>
            ))}
          </Box>
        ) : (
          // Desktop / tablet: classic ButtonGroup
          <ButtonGroup
            variant="outlined"
            sx={{
              '& .MuiButton-root': {
                textTransform: 'none',
              },
            }}
          >
            {Object.entries(capitalViews).map(([key, cfg]) => (
              <Button key={key} onClick={() => handleCapitalFlyTo(key)}>
                {cfg.label}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </Box>
    </MapStyles>
  );
}
